<script setup lang="ts">
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import type Swiper from 'swiper'
  import '@/assets/css/swiper.css'
  import type { IClpInfluencerDestinations } from '@/components/clp/influencer-block/types'
  import { SWIPER_NEXT_EL_ID, SWIPER_PREV_EL_ID } from '@/components/clp/constants'

  const props = defineProps<{ slides: IClpInfluencerDestinations[]; title: string }>()

  const sliderSettings: SwiperSettings = {
    speed: 500,
    keyboard: false,
    slidesPerView: 1,
    spaceBetween: '32px',
    noSwiping: true,
    allowTouchMove: false,
    navigation: {
      nextEl: '#dest-next-btn',
      prevEl: '#dest-prev-btn'
    },
    watchSlidesProgress: true,
    virtual: {
      enabled: true,
      cache: true
    },
    a11y: {
      slideLabelMessage: ''
    }
  }

  const activeSlide = ref(props.slides[0])
  const destinationAnnouncement = ref('')

  function setDestinationAnnouncement(destination: IClpInfluencerDestinations | undefined) {
    if (!destination) {
      destinationAnnouncement.value = ''
      return
    }

    destinationAnnouncement.value = `Destination changed to ${destination.destinCity}`
  }

  function getCurrentSwiperInstance(swiper: HTMLElement[]): Swiper | null {
    const activeSlide = swiper.find((slideNode) => slideNode.classList.contains('swiper-slide-visible'))
    if (!activeSlide && isDev()) {
      console.warn('No visible slide found, `watchSlidesProgress` might be disabled or multiple slides are visible')
    }

    const sliderDOM = activeSlide?.querySelector('swiper-container')

    if (sliderDOM && 'swiper' in sliderDOM) {
      return sliderDOM.swiper as Swiper
    }

    return null
  }

  function beforeSlideChange(swiper: CustomEvent<Swiper[]>) {
    const sliderInstance = getCurrentSwiperInstance(swiper.detail[0].slides)

    if (sliderInstance) {
      sliderInstance.el.setAttribute('aria-live', 'off')
      sliderInstance.slideTo(0)
      sliderInstance.update()
    }
  }

  function reInitSlider(swiper: CustomEvent<Swiper[]>) {
    const sliderInstance = getCurrentSwiperInstance(swiper.detail[0].slides)

    if (sliderInstance) {
      sliderInstance.el.setAttribute('aria-live', 'off')
      sliderInstance.navigation.init()
      sliderInstance.navigation.update()
    }
  }

  function onActiveIndexChange(swiper: CustomEvent<Swiper[]>) {
    const activeIndex = swiper.detail[0]?.activeIndex

    if (typeof activeIndex === 'number') {
      activeSlide.value = props.slides[activeIndex]
      setDestinationAnnouncement(activeSlide.value)
    }

    const sliderInstance = getCurrentSwiperInstance(swiper.detail[0].slides)

    if (sliderInstance) {
      sliderInstance.navigation.destroy()
    }
  }
</script>
<template>
  <div>
    <output
      class="sr-only"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ destinationAnnouncement }}
    </output>
    <div class="container mb-6 flex justify-between lg:mb-8">
      <div>
        <p class="mb-2 text-grey-dark text-subtitle-1 lg:mb-4 lg:text-h5">{{ props.title }}</p>
        <div class="flex flex-col lg:flex-row">
          <h2 class="mb-2 mr-2 text-bt-blue-dark lg:mb-0">{{ activeSlide.destinCity }}</h2>
          <div class="flex items-center">
            <span
              class="mr-2 h-fit rounded-sm border border-divider px-2 py-0.5 uppercase text-grey-dark text-subtitle-2"
            >
              {{ activeSlide.destinCode }}
            </span>
            <span class="text-grey-dark text-body-2">{{ activeSlide.destinCountry }}</span>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-4 lg:gap-8">
        <button
          id="dest-prev-btn"
          class="group z-5 cursor-pointer rounded-full border border-transparent bg-grey p-3 lg:border-bt-blue-dark lg:disabled:border-divider"
          type="button"
        >
          <Icon
            name="common/chevron-left"
            class="text-bt-blue-dark group-disabled:text-divider"
          />
        </button>
        <button
          id="dest-next-btn"
          class="group z-5 cursor-pointer rounded-full border border-transparent bg-grey p-3 lg:border-bt-blue-dark lg:disabled:border-divider"
          type="button"
        >
          <Icon
            name="common/chevron-right"
            class="text-bt-blue-dark group-disabled:text-divider"
          />
        </button>
      </div>
    </div>
    <div class="container">
      <slot
        name="top"
        v-bind="activeSlide"
      />
    </div>
    <div
      id="destination-slider-wrapper"
      class="relative pl-5 lg:container lg:pl-0"
    >
      <LazyUiSliderComponent
        :slider-settings="sliderSettings"
        :slides="slides"
        class="relative"
        @swiperactiveindexchange.self="onActiveIndexChange"
        @swiperslidechangeend.self="reInitSlider"
        @swiperslidechangetransitionstart.self="beforeSlideChange"
      >
        <template #slide="{ slide }">
          <slot v-bind="slide" />
        </template>
      </LazyUiSliderComponent>
      <div class="absolute -left-5 top-[calc(50%-2rem)] hidden w-[calc(100%+2.5rem)] justify-between xl:flex">
        <button
          :id="SWIPER_PREV_EL_ID"
          class="prev-btn z-5 cursor-pointer rounded-full border border-transparent bg-grey p-3 disabled:hidden"
        >
          <Icon
            name="common/chevron-left"
            class="text-bt-blue-dark"
          />
        </button>
        <button
          :id="SWIPER_NEXT_EL_ID"
          class="z-5 ml-auto cursor-pointer rounded-full border border-transparent bg-grey p-3 disabled:hidden"
        >
          <Icon
            name="common/chevron-right"
            class="text-bt-blue-dark"
          />
        </button>
      </div>
    </div>
    <div class="container">
      <slot
        name="footer"
        v-bind="activeSlide"
      />
    </div>
  </div>
</template>
